import { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
} from "@mui/material";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Link as SendIcon,
  InfoOutlined as InfoIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";

import { SponsorsDict } from "../../types/sponsors";

import { TitledPage } from "../../components/titled-page";
import { CampaignDoc, CampaignsDict } from "../../types/recruiting";
import SimpleLinkDialog from "../dialogs/dialogSimpleLink";
import { AuthContext } from "../../components/auth-provider";
import {
  deleteCampaign,
  loadCampaignNumbers,
  loadCampaigns,
  loadCandidates,
  saveCampaign,
} from "../../data-functions/recruiting_api";
import { loadSponsors } from "../../data-functions/system-data_api";
import { AgentDoc, AgentsDict } from "../../types/agents";
import { loadAgents } from "../../data-functions/agent-api";
import { isEqual } from "lodash";
import { LanguageSwitcher } from "../../components/language-switcher";
import { SupportedLanguages } from "../../languages/languages";
import { CustomSwitch } from "../../components/custom-switch";
import { DocumentHistoryDialog } from "../dialogs/dialogDocumentHistory";
import { hasRole } from "../../utils/user-utils";
import { Country } from "../../types/commons";
import { SelectCountryUntranslated } from "../../components/select-country-untranslated";
import QuestionsScores from "../../components/recruiting/questions-scores";

const RecruitingCampaigns: React.FC = () => {
  const [sponsors, setSponsors] = useState<SponsorsDict>({});
  const [campaigns, setCampaigns] = useState<CampaignsDict>({});
  const [campaignNumbers, setCampaignNumbers] = useState<{
    [key: string]: number;
  }>({});
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignDoc | null>(
    null
  );
  const [openAlert, setOpenAlert] = useState(false);
  const [currentLink, setCurrentLink] = useState("");

  const [adminSponsorId, setAdminSponsorId] = useState<string>("");
  const [agents, setAgents] = useState<AgentsDict>({});
  const [userAgent, setUserAgent] = useState<AgentDoc | null>(null);

  const [searchValue, setSearchValue] = useState<string>("");
  const [showOpen, setShowOpen] = useState<boolean>(true);

  const [openHistory, setOpenHistory] = useState(false);
  const [historyDoc, setHistoryDoc] = useState<string>("");

  const [errors, setErrors] = useState<{
    name: string;
    sponsor: string;
    agent: string;
    emailProviderUrl: string;
    country: string;
    minAge: string;
    maxAge: string;
    cooldownMonths: string;
  }>({
    name: "",
    sponsor: "",
    agent: "",
    emailProviderUrl: "",
    country: "",
    minAge: "",
    maxAge: "",
    cooldownMonths: "",
  });

  const { currentUser } = useContext(AuthContext)!;
  const [openScores, setOpenScores] = useState(false);

  const fetchSponsors = async () => {
    const sponsorsDict: SponsorsDict = await loadSponsors(currentUser?.appUser);
    setSponsors(sponsorsDict);
  };

  const fetchCampaigns = async () => {
    const campaignsDict: CampaignsDict = await loadCampaigns(
      currentUser?.appUser
    );
    setCampaigns(campaignsDict);
    const newNumbers: { [key: string]: number } = {};
    const campaignPromises = Object.entries(campaignsDict).map(
      async ([campaignId, _]) => {
        newNumbers[campaignId] = await loadCampaignNumbers(campaignId);
      }
    );
    await Promise.all(campaignPromises);
    setCampaignNumbers(newNumbers);
  };

  const fetchAgents = async () => {
    const agentsDict = await loadAgents(currentUser?.appUser);
    const agentId = currentUser?.appUser?.agentId || "";
    if (agentId && agentsDict) {
      console.log("user agent: " + agentId, agentsDict[agentId]);
      setUserAgent({ id: agentId, agent: agentsDict[agentId] });
    } else setUserAgent(null);
    setAgents(agentsDict);
  };

  useEffect(() => {
    fetchSponsors();
    fetchCampaigns();
    fetchAgents();
    const sponsorId = currentUser?.appUser?.sponsorId || "";
    setAdminSponsorId(sponsorId);

    setSelectedCampaign(null);
  }, [currentUser]);

  const handleAddCampaign = async () => {
    const agentId = userAgent?.id || "";
    // if (!agentId) {
    //   window.alert("Campaigns can only be created by users assigned to an agent!");
    //   return;
    // }

    const sponsorId = adminSponsorId || userAgent?.agent?.sponsorId || "";
    const campaign = {
      name: "New Campaign",
      sponsorId,
      agentId,
      status: "new",
      language: "en",
    };

    setSelectedCampaign({ id: "", campaign });
  };

  const handleSaveCampaign = async () => {
    if (!(selectedCampaign && validate())) {
      return;
    }

    const newCampaign = await saveCampaign(
      currentUser?.appUser,
      selectedCampaign,
      true
    );

    // Check if document exists
    if (newCampaign) {
      const newCampaigns = { ...campaigns };
      newCampaigns[newCampaign.id] = { ...newCampaign.campaign };
      setCampaigns(newCampaigns);
      setSelectedCampaign(newCampaign);
    } else {
      console.log(
        `No document found with email: ${selectedCampaign?.campaign?.name}`
      );
    }
  };

  const handleDelete = async (campaignId: string) => {
    if (!campaignId) return;

    const candidates = await loadCandidates(
      currentUser?.appUser,
      campaignId,
      {},
      3
    );
    if (Object.keys(candidates).length > 0) {
      window.alert(
        "This camapaign has candidates assosiated with it and cannot be deleted! Try setting its status to Closed instead."
      );
      return;
    }

    if (
      window.confirm(
        "Are you sure you want to delete this campaign? All candidates associated with the campaign will also be deleted!"
      )
    ) {
      deleteCampaign(campaignId).then(() => {
        const newCampaigns = { ...campaigns };
        delete newCampaigns[campaignId];
        setSelectedCampaign(null);
        setCampaigns(newCampaigns);
      });
    }
  };

  const handleSendLink = async (campaignId: string) => {
    if (!campaignId) return;

    const host = window.location.hostname;
    const baseUrl = host.endsWith("global-workforce.app")
      ? `https://${host}`
      : "https://visauk.realizon.tech";
    setCurrentLink(`${baseUrl}/register/${campaignId}`);
    setOpenAlert(true);
  };

  const handleAlertClose = () => {
    //    setEditMode(false);
    setOpenAlert(false);
  };

  const handleShowHistory = async (documentId: string) => {
    setHistoryDoc(documentId);
    setOpenHistory(true);
  };

  const handleSelectCampaign = (campaignId: string) => {
    if (campaignId) {
      const campaign = campaigns[campaignId];
      if (campaign) {
        if (selectedCampaign && selectedCampaign.id !== campaignId) {
          const sCampaign = selectedCampaign.id
            ? campaigns[selectedCampaign.id]
            : null;

          if (!isEqual(sCampaign, selectedCampaign.campaign)) {
            const conf = window.confirm(
              "The selected campaign has been changed! Press OK if you want to discard the changes."
            );
            if (!conf) {
              return;
            }
          }
        }

        console.log("campaign", { id: campaignId, campaign });
        setSelectedCampaign({ id: campaignId, campaign: { ...campaign } });
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //    if (event.target.name === "expiry_date") {
    if (selectedCampaign) {
      const newCampaign: CampaignDoc = {
        ...selectedCampaign,
        campaign: {
          ...selectedCampaign.campaign,
          [event.target.name]: event.target.value,
        },
      };
      console.log("newCampaign", newCampaign);
      setSelectedCampaign(newCampaign);
    }
  };

  const handleCheckboxClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedCampaign) {
      const newCampaign: CampaignDoc = {
        ...selectedCampaign,
        campaign: {
          ...selectedCampaign.campaign,
          [event.target.name]: event.target.checked,
        },
      };
      setSelectedCampaign(newCampaign);
    }
  };

  const handleToggleChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: string
  ) => {
    const key = (event.target as Element).id;

    if (key && selectedCampaign) {
      const newCampaign: CampaignDoc = {
        ...selectedCampaign,
        campaign: {
          ...selectedCampaign.campaign,
          status: newValue,
        },
      };
      console.log(key + ": " + newValue);
      setSelectedCampaign(newCampaign);
    }
  };

  const handleInputComboChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (selectedCampaign) {
      const value = event.target.value;

      const newCampaign: CampaignDoc = {
        ...selectedCampaign,
      };
      switch (event.target.name) {
        case "sponsorId":
          newCampaign.campaign.sponsorId = value;
          break;

        case "agentId":
          newCampaign.campaign.agentId = value;
          break;

        default:
          break;
      }

      setSelectedCampaign(newCampaign);
    }
  };

  const handleLanguageChange = (newLanguage: string | null) => {
    if (newLanguage && selectedCampaign) {
      const newCampaign: CampaignDoc = {
        ...selectedCampaign,
      };
      newCampaign.campaign.language = newLanguage;
      setSelectedCampaign(newCampaign);
    }
  };

  const handleCountryChange = (_: any, newValue: Country) => {
    if (selectedCampaign) {
      setSelectedCampaign({
        ...selectedCampaign,
        campaign: {
          ...selectedCampaign.campaign,
          country: newValue,
        },
      });
    }
  };

  const validate = () => {
    const newErrors = {
      name: "",
      sponsor: "",
      agent: "",
      emailProviderUrl: "",
      country: "",
      minAge: "",
      maxAge: "",
      cooldownMonths: "",
    };
    let isValid = true;
    if (!selectedCampaign) return false;
    console.log("selectedCampaign", selectedCampaign);

    if (!selectedCampaign.campaign.name) {
      newErrors.name = "Please, enter a valid name for this campaign!";
      isValid = false;
    }

    if (!selectedCampaign.campaign.sponsorId) {
      newErrors.sponsor = "Please, select a sponsor forthis campaign!";
      isValid = false;
    }

    if (
      selectedCampaign.campaign.restrictEmailProvider &&
      !(
        (
          selectedCampaign.campaign.emailProviderUrl &&
          selectedCampaign.campaign.emailProviderUrl.split(".").length === 2
        ) //only one dot allowed
      )
    ) {
      newErrors.emailProviderUrl =
        "Please, enter a valid email provider URL (e.g. gmail.com)!";
      isValid = false;
    }

    if (!selectedCampaign.campaign.country) {
      newErrors.country = "Please, select a country for this campaign!";
      isValid = false;
    }

    if (selectedCampaign.campaign.disqualifyIfAgeInvalid) {
      if (!selectedCampaign.campaign.minAge) {
        newErrors.minAge = "Please, enter a minimum age!";
        isValid = false;
      }
      if (!selectedCampaign.campaign.maxAge) {
        newErrors.maxAge = "Please, enter a maximum age!";
        isValid = false;
      }
    }

    if (selectedCampaign.campaign.disqualifyIfAlreadyRegistered) {
      if (!selectedCampaign.campaign.cooldownMonths) {
        newErrors.cooldownMonths = "Please, enter a grace period in months!";
        isValid = false;
      }
    }

    console.log("newErrors", newErrors);
    setErrors(newErrors);
    return isValid;
  };

  const getCampaignStatusChip = (status: string | undefined) => {
    switch (status) {
      case "open":
        return <Chip label="Open" color="success" />;

      case "closed_registration":
        return <Chip label="Registration Closed" color="warning" />;

      case "closed":
        return <Chip label="Closed" color="error" />;

      default:
        return <Chip label="New" color="primary" variant="outlined" />;
    }
  };

  /* ------------ Search functions -------------- */

  const handleSearchValueChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchValue(event.target.value);
  };

  const filteredCampaigns = useMemo(
    () =>
      Object.entries(campaigns).filter(([campaignId, campaign]) => {
        try {
          return (
            (!showOpen || campaign.status !== "closed") &&
            (searchValue.length < 3 ||
              campaign.name.toLowerCase().includes(searchValue.toLowerCase()) ||
              (campaign.status &&
                campaign.status
                  .toLowerCase()
                  .includes(searchValue.toLowerCase())))
          );
        } catch (err) {
          console.error("filter exception:");
          console.log(campaign);
          return false;
        }
      }),
    [searchValue, showOpen, campaigns]
  );

  return (
    <TitledPage title="Recruiting Campaigns">
      <Box>
        <Box display={"flex"} sx={{ gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleAddCampaign}
          >
            Add New Campaign
          </Button>
          {hasRole(currentUser?.appUser, ["admin"]) && (
            <>
              <DocumentHistoryDialog
                open={openHistory}
                onClose={() => setOpenHistory(false)}
                documentId={historyDoc}
              />
              <Tooltip title="History">
                <span>
                  <IconButton
                    color="primary"
                    edge={"end"}
                    sx={{ marginTop: "8px", marginBottom: "4px" }}
                    disabled={!selectedCampaign}
                    onClick={() =>
                      handleShowHistory(selectedCampaign?.id || "")
                    }
                  >
                    <InfoIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ marginTop: "8px", marginBottom: "4px" }}
              />
            </>
          )}
          <Tooltip
            title={showOpen ? "Show only open campaigns" : "Show all campaigns"}
          >
            <CustomSwitch
              checked={showOpen}
              onClick={() => setShowOpen(!showOpen)}
            />
          </Tooltip>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search..."
            value={searchValue}
            onChange={handleSearchValueChange}
            sx={{ minWidth: "250px" }}
          />
          <SimpleLinkDialog
            label={currentLink}
            open={openAlert}
            showQRCode={true}
            showSendLink={false}
            onSendLink={() => {}}
            onClose={handleAlertClose}
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "row", marginTop: "1rem" }}>
          <Box
            sx={{
              flexGrow: 1,
              flexBasis: "0",
              maxWidth: `calc(100% - 600px)`,
              marginRight: "8px",
            }}
          >
            <TableContainer component={Paper} sx={{ minHeight: "300px" }}>
              <Table sx={{ width: "100%" }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell component="th">Name</TableCell>
                    <TableCell component="th">Status</TableCell>
                    <TableCell component="th">
                      <Tooltip title="Number of registered candidates">
                        <span>#</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell component="th" align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.values(filteredCampaigns).map(
                    ([campaignId, campaign]) => {
                      return (
                        <TableRow
                          key={campaignId}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                          onClick={() => handleSelectCampaign(campaignId)}
                          className={
                            selectedCampaign?.id === campaignId
                              ? "selected"
                              : ""
                          }
                        >
                          <TableCell scope="row" size="small">
                            {campaign.name}
                          </TableCell>
                          <TableCell scope="row" size="small">
                            {getCampaignStatusChip(campaign.status)}
                          </TableCell>
                          <TableCell scope="row" size="small">
                            {campaignNumbers[campaignId] || 0}
                          </TableCell>
                          <TableCell
                            align="right"
                            style={{ width: 100 }}
                            size="small"
                          >
                            <Tooltip title="Send Link">
                              <IconButton
                                size="small"
                                onClick={() => handleSendLink(campaignId || "")}
                              >
                                <SendIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(campaignId || "")}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    }
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* -------- Campaign Details -------- */}
          <Box sx={{ width: "48%", maxWidth: "600px" }}>
            {selectedCampaign && (
              <Card>
                <CardHeader title={"Campaign Details"} />
                <CardContent>
                  <Grid container spacing={2} padding={2}>
                    {/* ----------- Sponsors ----------- */}
                    <Grid item xs={12}>
                      {!adminSponsorId && (
                        <TextField
                          name={"sponsorId"}
                          label={"Sponsor"}
                          select
                          error={!!errors.sponsor}
                          helperText={errors.sponsor}
                          onChange={handleInputComboChange}
                          value={
                            (selectedCampaign &&
                              selectedCampaign.campaign.sponsorId) ||
                            ""
                          }
                          required
                          fullWidth
                          margin={"dense"}
                        >
                          <MenuItem value="" key="no_sponsor">
                            <Typography fontStyle={"italic"}>
                              no sponsor selected
                            </Typography>
                          </MenuItem>
                          {Object.entries(sponsors).map(
                            ([sponsorId, sponsor]) => {
                              return (
                                <MenuItem value={sponsorId} key={sponsorId}>
                                  {sponsor.name}
                                </MenuItem>
                              );
                            }
                          )}
                        </TextField>
                      )}
                    </Grid>

                    {/* ----------- Agent ----------- */}
                    {userAgent && (
                      <Grid item xs={12}>
                        <Typography variant={"body1"}>
                          Agent: {userAgent.agent.company || "n/a"}
                        </Typography>
                      </Grid>
                    )}

                    {!userAgent && (
                      <Grid item xs={12}>
                        <TextField
                          name={"agentId"}
                          label={"Agent"}
                          select
                          error={!!errors.agent}
                          helperText={errors.agent}
                          onChange={handleInputComboChange}
                          value={
                            (selectedCampaign &&
                              selectedCampaign.campaign.agentId) ||
                            ""
                          }
                          fullWidth
                          margin={"dense"}
                          InputLabelProps={{ shrink: true }}
                        >
                          <MenuItem value="" key="no_agent">
                            <Typography fontStyle={"italic"}>
                              no agent selected
                            </Typography>
                          </MenuItem>
                          {Object.entries(agents).map(([agentId, agent]) => {
                            return (
                              <MenuItem value={agentId} key={agentId}>
                                {agent.company}
                              </MenuItem>
                            );
                          })}
                        </TextField>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <TextField
                        error={!!errors.name}
                        helperText={errors.name}
                        autoFocus
                        margin="dense"
                        name="name"
                        label="Name"
                        type="text"
                        fullWidth
                        value={
                          selectedCampaign && selectedCampaign.campaign.name
                        }
                        onChange={handleInputChange}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    {/* ----------------- Country ---------------- */}
                    <Grid item xs={12}>
                      <SelectCountryUntranslated
                        id="country"
                        label="Country"
                        value={
                          selectedCampaign.campaign.country || {
                            code: "",
                            label: "",
                          }
                        }
                        onChange={handleCountryChange}
                      />
                    </Grid>

                    {/* ----------------- Show Region ---------------- */}
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="includeRegion"
                            checked={
                              selectedCampaign.campaign.includeRegion || false
                            }
                            onChange={handleCheckboxClick}
                          />
                        }
                        label="Include Region"
                      />
                    </Grid>

                    {/* ----------------- Show University ---------------- */}
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="includeUniversity"
                            checked={
                              selectedCampaign.campaign.includeUniversity ||
                              false
                            }
                            onChange={handleCheckboxClick}
                            disabled={
                              selectedCampaign.campaign.country?.code !== "KE"
                            }
                            sx={{ marginLeft: "16px" }}
                          />
                        }
                        label="Include University"
                      />
                    </Grid>

                    {/* ----------------- Language ---------------- */}
                    <Grid item xs={12}>
                      <Typography variant="body1">
                        Preferred language:{" "}
                        <LanguageSwitcher
                          initialLanguage={
                            selectedCampaign &&
                            selectedCampaign.campaign &&
                            selectedCampaign.campaign.language
                              ? (selectedCampaign.campaign
                                  .language as SupportedLanguages)
                              : "en"
                          }
                          onLanguageChange={handleLanguageChange}
                        />
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={7}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="showPaymentsDisclaimer"
                            checked={
                              selectedCampaign.campaign
                                .showPaymentsDisclaimer || false
                            }
                            onChange={handleCheckboxClick}
                          />
                        }
                        label="Show Payments Disclaimer"
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="showAdvancedForm"
                            checked={
                              selectedCampaign.campaign.showAdvancedForm ||
                              false
                            }
                            onChange={handleCheckboxClick}
                          />
                        }
                        label="Show Advanced Form"
                      />
                      <Button
                        variant="outlined"
                        sx={{ ml: 2 }}
                        disabled={!selectedCampaign.campaign.showAdvancedForm}
                        onClick={() => setOpenScores(true)}
                      >
                        Set scores
                      </Button>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="askForSpouseApplication"
                            checked={
                              selectedCampaign.campaign
                                .askForSpouseApplication || false
                            }
                            onChange={handleCheckboxClick}
                          />
                        }
                        label="Ask for Travel companion"
                      />
                      <Typography variant="body1" sx={{ fontSize: "0.8rem" }}>
                        If enabled, candidates will be asked about a travel
                        companion (travelling alone, with spouse, or with a
                        relative/friend). Additional fields will be displayed
                        accordingly.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="showCurrentLocation"
                            checked={
                              selectedCampaign.campaign.showCurrentLocation ||
                              false
                            }
                            onChange={handleCheckboxClick}
                          />
                        }
                        label="Show Current Location Question"
                      />
                    </Grid>

                    <Grid item xs={12} md={7}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="disqualifyIfNoPassport"
                            checked={
                              selectedCampaign.campaign
                                .disqualifyIfNoPassport || false
                            }
                            onChange={handleCheckboxClick}
                          />
                        }
                        label="Disqualify if no passport"
                      />
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="requirePassportScan"
                            checked={
                              selectedCampaign.campaign.requirePassportScan ||
                              false
                            }
                            onChange={handleCheckboxClick}
                          />
                        }
                        label="Require Image Upload"
                      />
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="disqualifyIfAgeInvalid"
                            checked={
                              selectedCampaign.campaign
                                .disqualifyIfAgeInvalid || false
                            }
                            onChange={handleCheckboxClick}
                          />
                        }
                        label="Disqualify if age invalid"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        error={!!errors.minAge}
                        helperText={errors.minAge}
                        margin="dense"
                        name="minAge"
                        label="Minimum Age"
                        type="number"
                        fullWidth
                        value={
                          selectedCampaign && selectedCampaign.campaign.minAge
                        }
                        onChange={handleInputChange}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        error={!!errors.maxAge}
                        helperText={errors.maxAge}
                        margin="dense"
                        name="maxAge"
                        label="Maximum Age"
                        type="number"
                        fullWidth
                        value={
                          selectedCampaign && selectedCampaign.campaign.maxAge
                        }
                        onChange={handleInputChange}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} md={8}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="disqualifyIfAlreadyRegistered"
                            checked={
                              selectedCampaign.campaign
                                .disqualifyIfAlreadyRegistered || false
                            }
                            onChange={handleCheckboxClick}
                          />
                        }
                        label="Disqualify if registered too soon"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        error={!!errors.cooldownMonths}
                        helperText={errors.cooldownMonths}
                        margin="dense"
                        name="cooldownMonths"
                        label="Grace Period (months)"
                        type="number"
                        fullWidth
                        value={
                          selectedCampaign &&
                          selectedCampaign.campaign.cooldownMonths
                        }
                        onChange={handleInputChange}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    {/* ----------------- Automatic Invitations ---------------- */}

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="inviteAutomatically"
                            checked={
                              selectedCampaign.campaign.inviteAutomatically ||
                              false
                            }
                            onChange={handleCheckboxClick}
                          />
                        }
                        label="Invite Automatically"
                      />
                      <Typography variant="body1" sx={{ fontSize: "0.8rem" }}>
                        Automatically invite candidates to an interview, after
                        they verify their registration successfully. This will
                        send an email to the candidate with a confirmation code
                        for the interview.
                      </Typography>
                    </Grid>

                    {/* ----------------- Verification Type ---------------- */}

                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <FormLabel>Verification Type</FormLabel>
                        <RadioGroup
                          row
                          name="verificationType"
                          value={
                            (selectedCampaign &&
                              selectedCampaign.campaign &&
                              selectedCampaign.campaign.verificationType) ||
                            "email"
                          }
                          onChange={(e) => handleInputChange(e as any)}
                        >
                          <FormControlLabel
                            value="email"
                            control={<Radio />}
                            label="Email"
                          />
                          <FormControlLabel
                            value="automatic"
                            control={<Radio />}
                            label="Automatic"
                          />
                          <FormControlLabel
                            value="google"
                            control={<Radio />}
                            label="Google login"
                          />
                        </RadioGroup>
                      </FormControl>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          marginTop: "8px",
                          gap: 2,
                        }}
                      >
                        <WarningIcon
                          sx={{
                            mr: 0.5,
                            verticalAlign: "text-bottom",
                            color: "error.main",
                          }}
                        />
                        <Typography
                          variant="body1"
                          sx={{ fontSize: "0.8rem", color: "error.main" }}
                        >
                          Please, note that email verification for campaings
                          with expected candidates above 20K may result inissues
                          with the candidates receiving these emails, as the
                          providers will block the huge amount of emails as
                          spam.
                        </Typography>
                      </Box>
                    </Grid>

                    {/* ----------------- Restrict Email Provider ---------------- */}
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="restrictEmailProvider"
                            checked={
                              selectedCampaign.campaign.restrictEmailProvider ||
                              false
                            }
                            onChange={handleCheckboxClick}
                          />
                        }
                        label="Restrict Email Provider"
                      />
                      <TextField
                        error={!!errors.emailProviderUrl}
                        helperText={errors.emailProviderUrl}
                        margin="dense"
                        name="emailProviderUrl"
                        label="Provider URL (e.g. gmail.com)"
                        type="text"
                        disabled={
                          !selectedCampaign.campaign.restrictEmailProvider
                        }
                        fullWidth
                        value={
                          (selectedCampaign &&
                            selectedCampaign.campaign.emailProviderUrl) ||
                          ""
                        }
                        onChange={handleInputChange}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    {/* ----------------- Status ---------------- */}
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <FormLabel>Status</FormLabel>
                        <ToggleButtonGroup
                          id="status"
                          value={
                            (selectedCampaign &&
                              selectedCampaign.campaign.status) ||
                            "new"
                          }
                          exclusive
                          onChange={handleToggleChange}
                          aria-label="Campaign Status"
                          sx={{
                            marginTop: "8px",
                          }}
                        >
                          <ToggleButton
                            id="status_new"
                            value="new"
                            sx={{
                              "&.Mui-selected": {
                                backgroundColor: (theme) =>
                                  theme.palette.primary.main,
                                color: (theme) =>
                                  theme.palette.primary.contrastText,
                                "&:hover": {
                                  backgroundColor: (theme) =>
                                    theme.palette.primary.dark,
                                },
                              },
                            }}
                          >
                            New
                          </ToggleButton>
                          <ToggleButton
                            id="status_open"
                            value="open"
                            sx={{
                              "&.Mui-selected": {
                                backgroundColor: (theme) =>
                                  theme.palette.success.main,
                                color: (theme) =>
                                  theme.palette.success.contrastText,
                                "&:hover": {
                                  backgroundColor: (theme) =>
                                    theme.palette.success.dark,
                                },
                              },
                            }}
                          >
                            Open
                          </ToggleButton>
                          <ToggleButton
                            id="status_closed_registration"
                            value="closed_registration"
                            sx={{
                              "&.Mui-selected": {
                                backgroundColor: (theme) =>
                                  theme.palette.warning.main,
                                color: (theme) =>
                                  theme.palette.warning.contrastText,
                                "&:hover": {
                                  backgroundColor: (theme) =>
                                    theme.palette.warning.dark,
                                },
                              },
                            }}
                          >
                            Registration Closed
                          </ToggleButton>
                          <ToggleButton
                            id="status_closed"
                            value="closed"
                            sx={{
                              "&.Mui-selected": {
                                backgroundColor: (theme) =>
                                  theme.palette.error.main,
                                color: (theme) =>
                                  theme.palette.error.contrastText,
                                "&:hover": {
                                  backgroundColor: (theme) =>
                                    theme.palette.error.dark,
                                },
                              },
                            }}
                          >
                            closed
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
                <CardActions sx={{ padding: 2 }}>
                  <Box flexGrow={1}></Box>
                  <Button
                    variant="contained"
                    onClick={handleSaveCampaign}
                    sx={{ mr: 2 }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSelectedCampaign(null);
                    }}
                    sx={{ mr: 2 }}
                  >
                    Cancel
                  </Button>
                </CardActions>
                <QuestionsScores
                  open={openScores}
                  language={selectedCampaign.campaign.language}
                  initialScores={selectedCampaign.campaign.surveyQuestionScores}
                  onClose={() => setOpenScores(false)}
                  onSave={(newScores) => {
                    if (!selectedCampaign) return;
                    const updated = {
                      ...selectedCampaign,
                      campaign: {
                        ...selectedCampaign.campaign,
                        surveyQuestionScores: newScores,
                      },
                    };
                    setSelectedCampaign(updated);
                  }}
                />
              </Card>
            )}
          </Box>
        </Box>
      </Box>
    </TitledPage>
  );
};

export default RecruitingCampaigns;
